// ============================================================================
// APPLICATION TYPES
// ============================================================================

export type ApplicationType =
  | "CCM1"
  | "CCM2"
  | "CCM3"
  | "CM3"
  | "PM"
  | "DCCM"
  | "SPM";

export type StatusType =
  | "Draft"
  | "SPM Review"
  | "DM Review"
  | "VP Review"
  | "Ops Review"
  | "Paid"
  | "Rejected"
  | "Cancelled";

export type UserAction = "Draft" | "Submitted";

// ============================================================================
// BONUS APPLICATION INTERFACES
// ============================================================================

export interface BonusApplication {
  bonusApplicationId: number;
  applicationId: number;
  employeeId: number;
  applicationTypeId: number;
  statusId: number;
  year: number;
  quarter: number;
  amount: number | null;
  projectNumbers: string | null;
  requestDate: string | null;
  isUserInvolved: boolean;
  isActive: boolean;
  employeeName: string;
  applicationType: ApplicationType;
  currentStatus: string;
  status: string;
  userAction?: UserAction;
  hasOverriddenHierarchy: boolean;
  
  // User Names
  submittedByName: string;
  bonusRecipientName: string | null;
  spmReviewedByName: string | null;
  dccmReviewedByName: string | null;
  dmReviewedByName: string | null;
  vpReviewedByName: string | null;
  opsReviewedByName: string | null;
  payrollProcessedByName: string | null;
  rejectedByName: string | null;
  cancelledByName: string | null;
  createdByName: string;
  updatedByName: string | null;

  // User Emails
  submittedBy: string;
  bonusRecipientEmail?: string | null;
  spmReviewedBy: string | null;
  dccmReviewedBy: string | null;
  dmReviewedBy: string | null;
  vpReviewedBy: string | null;
  opsReviewedBy: string | null;
  payrollProcessedBy: string | null;
  rejectedBy: string | null;
  cancelledBy: string | null;
  createdBy: string;
  updatedBy: string | null;

  // Audit timestamps
  submittedOn: Date | string | null;
  bonusRecipientReviewedOn: Date | string | null;
  spmReviewedOn: Date | string | null;
  dccmReviewedOn: Date | string | null;
  dmReviewedOn: Date | string | null;
  vpReviewedOn: Date | string | null;
  opsReviewedOn: Date | string | null;
  payrollProcessingOn: Date | string | null;
  paymentDate: Date | string | null;
  rejectedOn: Date | string | null;
  cancelledOn: Date | string | null;
  createdOn: Date | string;
  updatedOn: Date | string | null;

  paymentReference: string | null;
  proxySubmitterEmailAddress: string | null;

  // Optional UI-specific fields
  isChecked?: boolean;
  comments?: BonusApplicationComment[];
  bonusSheet?: string;

  rejectionReason: string | null;
  rejectedAt: string | null;

  location: string | null;
}

export interface BonusApplicationDetails {
  bonusApplicationId: number;
  username: string;
  applicationType: ApplicationType;
  status: string;
  createdDate: string;
  currentStatus: string;
  employeeName: string;
  createdBy: string;
  projectNumbers: string;
  historyItems: BonusApplicationHistoryItem[];
  comments: BonusApplicationComment[];
  ProxySubmitterEmailAddress: string | null;
}

export interface BonusDetailsResponse {
  bonusApplicationId: number;
  year: number;
  quarter: number;
  amount: number;
  projectNumbers: string;
  requestDate: string | null;
  isUserInvolved: boolean;
  bonusRecipientEmail: string;
  employeeName: string;
  applicationType: string;
  hasOverriddenHierarchy: boolean;
  currentStatus: string;
  proxySubmitterEmailAddress: string;
  
  // Reviewer Names
  submittedByName: string;
  spmReviewedByName: string;
  dccmReviewedByName: string;
  dmReviewedByName: string;
  vpReviewedByName: string;
  opsReviewedByName: string;
  payrollProcessedByName: string;
  rejectedByName: string;
  cancelledByName: string;
  createdByName: string;
  updatedByName: string;
  
  // Reviewer Emails
  submittedBy: string;
  spmReviewedBy: string;
  dccmReviewedBy: string;
  dmReviewedBy: string;
  vpReviewedBy: string;
  opsReviewedBy: string;
  payrollProcessedBy: string;
  rejectedBy: string;
  cancelledBy: string;
  createdBy: string;
  updatedBy: string;
  
  // Timestamps
  submittedOn: string | null;
  spmReviewedOn: string | null;
  dccmReviewedOn: string | null;
  dmReviewedOn: string | null;
  vpReviewedOn: string | null;
  opsReviewedOn: string | null;
  payrollProcessedOn: string | null;
  paymentDate: string | null;
  rejectedOn: string | null;
  cancelledOn: string | null;
  createdOn: string | null;
  updatedOn: string | null;
  
  // Additional Data
  paymentReference: string;
  bonusSheet: BonusSheet; 
  comments: BonusApplicationComment[];
  historyItems: BonusApplicationHistoryItem[];
}


export interface BonusSheet {
  id: number;
  bonusTemplateId: number | null;
  role: string | null;
  jsonData: string | null;
  createdBy: string | null;
  createdOn: string | null;
  updatedBy: string | null;
  updatedOn: string | null;
  bonusApplicationId: number;
  isActive: boolean;
  applicationTypeId: number;
  // Computed columns (persisted)
  applicantEmail: string | null;
  year: number | null;
  quarter: number | null;
  totalBonusPayout: number | null;
  totalScore: number | null;
  bonusPercentage: number | null;
  hasFoundationCalls: boolean;
}
// ============================================================================
// COMMENT INTERFACES
// ============================================================================

export interface BonusApplicationComment {
  commentId: number;
  bonusApplicationId: number;
  commentText: string;
  commentedBy: string;
  commentedOn: string;
  commentedByEmail: string;
  statusAtTimeOfComment: string;
  statusIdAtTimeOfComment: number;
  roleTypeName: string;
  roleTypeId: number;
  isActive: boolean;
  isSystemGenerated: boolean;
  historyId?: number | null;
}

// ============================================================================
// HISTORY INTERFACES
// ============================================================================

export interface BonusApplicationHistoryItem {
  historyId: number;
  bonusApplicationId: number;
  statusId: number;
  previousStatusId: number | null;
  
  // Status Information
  currentStatusName: string;
  currentStatusDescription: string | null;
  previousStatusName: string | null;
  previousStatusDescription: string | null;
  
  // Completion Information
  completedByEmail: string | null;
  completedOn: string | null; // ISO date string
  changedBy: string | null;
  changedOn: string; // ISO date string
  changeType: string;

  comment: string | null;
  createdBy: string | null;
  createdOn: string; // ISO date string
  updatedBy: string | null;
  updatedOn: string | null; // ISO date string
  
  // Additional Metadata
  amount: number | null;
  year: number;
  quarter: number;
  requestDate: string | null;
  paymentReference: string | null;
  
  // Original Tracking
  originalCreatedOn: string | null;
  originalCreatedBy: string | null;
  originalUpdatedOn: string | null;
  originalUpdatedBy: string | null;
  
  // Bulk Action Information
  isBulkAction: boolean;
  bulkActionPerformedBy: string | null;
  bulkActionPerformedOn: string | null;
  
  // Deduplication Helper
  statusOccurrenceNumber: number;
  isLatestForStatus: boolean;
}

export interface StatusTransition {
  fromStatusId: number | null;
  toStatusId: number;
  fromStatusName: string | null;
  toStatusName: string;
  fromStatusLabel: string | null;
  toStatusLabel: string;
}

export interface FieldChange {
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface TimelineComment {
  commentId: number;
  text: string;
  commentedBy: string;
  commentedByEmail: string;
  timestamp: string; // ISO date string
  roleType: string;
  isSystemGenerated: boolean;
}

export interface TimelineEvent {
  eventType: string; // "Created", "StatusChange", "Approval", "Rejection", "Cancellation"
  timestamp: string; // ISO date string
  
  // Status Transition
  statusTransition: StatusTransition | null;
  
  // Actor Information
  actorEmail: string | null;
  actorName: string | null;
  approvalRole: string | null;
  
  // Change Details
  fieldChanges: FieldChange[] | null;
  isBulkAction: boolean;
  bulkActionPerformedBy: string | null;
  note: string | null;
  
  // Associated Comments
  comments: TimelineComment[];
  
  // Completion Status
  isCompleted: boolean;
}

export interface WorkflowStatus {
  statusId: number;
  statusName: string;
  displayLabel: string;
  isCompleted: boolean;
  isCurrent: boolean;
  completedOn: string | null;
  completedBy: string | null;
  completedByName: string | null;
}

export interface BonusApplicationReviewHistory {
  bonusApplicationId: number;
  employeeName: string;
  applicationType: string;
  currentStatus: string;
  totalAmount: number;
  year: number;
  quarter: number;
  
  // Timeline of all events (history + comments merged)
  timeline: TimelineEvent[];
  
  // All comments separately (for the Comments section)
  allComments: BonusApplicationComment[];
}

export interface Employee {
  EmployeeNumber: string;
  EmployeeName: string;
  EmployeeEmail: string;
  ApplicationType?: string;
  JobTitle: string;
  JobFamily: string;
  EmploymentStatus: string;
  SupervisorName: string;
  SupervisorEmail: string;
  HasFoundationCalls: boolean;
}

export interface GetEmployeesParams {
  // jobFamily?: string;
  // applicationType?: string;
  // isExEmployee?: boolean;
}

export interface GetEmployeesResponse {
  employees: Employee[];
}

export interface GetOverrideEmployeesParams {
  jobFamily?: string;
}

export interface GetOverrideEmployeesResponse {
  employees: Employee[];
}


// ============================================================================
// PROJECT INTERFACES
// ============================================================================

export interface Project {
  projectId: number;
  projectName: string;
  projectNumber: string;
  hierLevel1Number: string;
  hierLevel1: string;
  hierLevel2: string;
  hierLevel3: string;
  hierLevel4: string;
  hierLevel5: string;
  hierLevel6: string;
  hierLevel7: string | null;
  hierLevel8: string | null;
  isPmSkipped: boolean;
  isSpmSkipped: boolean;
  skipReviews: {
    skipReviewId: number;
    projectId: number;
    reviewStepId: number;
  }[];
}

// ============================================================================
// BONUS SHEET & TEMPLATE INTERFACES
// ============================================================================

export interface DistributionRule {
  role: string;
  percentage: number;
}

export interface zBonusSheet {
  bonusType: "ClosingBonus" | string;
  period: string;
  totalAmount: number;
  distributionRules: DistributionRule[];
  notes: string;
}

export interface BonusTemplate {
  id: number;
  name: string;
}

export interface Template {
  id: number;
  name: string;
}

// ============================================================================
// FILE ATTACHMENT INTERFACES
// ============================================================================

export interface FileAttachment {
  id: number;
  fileName: string;
  contentType: string;
  employeeId?: number;
  employeeName?: string;
  email?: string;
  quarter?: string;
  projectId?: number;
  projectName?: string;
  bonusApplicationId?: number;
  createdOn?: string | null;
  createdBy?: string | null;
}

// ============================================================================
// API REQUEST/RESPONSE INTERFACES
// ============================================================================

export interface BonusQueryParams {
  year: number | "All";
  quarter: number | "All";
}

export interface EditBonusApplicationPayload {
  BonusApplicationId: number;
  UserAction:
    | "Approved"
    | "Rejected"
    | "Draft"
    | "Submitted"
    | "Payroll_Processing"
    | "Paid";
  Amount?: number | null;
  BonusSheet: string | null;
  ExEmployee: string | null;
  Comments?: string[];
}

export interface BonusApplicationApiResponse {
  data: BonusApplication[] | any[];
  error?: string | null;
}

export interface ActionItem {
  id: number;
  status: string;
}

export interface UserReview {
  id: number;
  status: string;
}

export interface BonusResponse {
  bonusRecordId: number;
  projectNumbers: number;
  status: string;
  createdDate: string;
}

export interface BonusRecord {
  bonusRecordId: number;
  projectNumbers: number;
  status: string;
  createdDate: string;
}

// ============================================================================
// STATUS TIMELINE
// ============================================================================

export interface TimelineStep {
  id: number;
  name: StatusType;
  activeColor: string;
  defaultColor: string;
}

export const statusTimeline: TimelineStep[] = [
  { id: 1, name: "Draft", activeColor: "#87CEEB", defaultColor: "#E5E7EB" },
  { id: 2, name: "SPM Review", activeColor: "#4682B4", defaultColor: "#E5E7EB" },
  { id: 3, name: "DM Review", activeColor: "#008B8B", defaultColor: "#E5E7EB" },
  { id: 4, name: "VP Review", activeColor: "#FFA500", defaultColor: "#E5E7EB" },
  { id: 5, name: "Ops Review", activeColor: "#FF7F50", defaultColor: "#E5E7EB" },
  { id: 6, name: "Paid", activeColor: "#228B22", defaultColor: "#E5E7EB" },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================


export const DEFAULT_JOB_FAMILIES = 'CONSTRUCTION,CUSTOMER CARE,TALENT & LEADERSHIP DEV.';

export const getStatusColor = (status: string): string => {
  const step = statusTimeline.find((s) => s.name === status);
  return step?.activeColor || "#E5E7EB";
};

export const getStatusIndex = (status: string): number => {
  return statusTimeline.findIndex((s) => s.name === status);
};

export function isStatusChangeEvent(event: TimelineEvent): boolean {
  return event.eventType === 'StatusChange' || event.eventType === 'Approval';
}

export function hasComments(event: TimelineEvent): boolean {
  return event.comments && event.comments.length > 0;
}

export function formatStatusName(statusName: string): string {
  return statusName.replace(/_/g, ' ');
}

export function getEventIconType(event: TimelineEvent): 'completed' | 'current' | 'pending' {
  if (!event.isCompleted) return 'pending';
  if (event.eventType === 'Rejection' || event.eventType === 'Cancellation') return 'current';
  return 'completed';
}

export function getEventColor(eventType: string): string {
  switch (eventType) {
    case 'Created':
    case 'StatusChange':
    case 'Approval':
      return 'blue';
    case 'Rejection':
      return 'red';
    case 'Cancellation':
      return 'orange';
    default:
      return 'gray';
  }
}